import { useAppSelector } from '../data/store';
import { useEffect, useRef, useState } from 'react';
import { getVideoFilterIndex } from '../domain/player-provider';
import { useWindowResizeObserver } from '../../../../domain/animations/animations-provider';

export const CONTAINER_SIZE = '550px';

const Video = () => {

    const boxRef = useRef<HTMLDivElement>(null);
    const videoRef = useRef<HTMLVideoElement>(null);

    const data = useAppSelector(store => store.root.data);
    const selected = useAppSelector(store => store.root.selected);
    const isPlaying = useAppSelector(store => store.root.isPlaying);
    const isBypass = useAppSelector(store => store.root.isBypass);
    const isDelta = useAppSelector(store => store.root.isDelta);

    const [height, setHeight] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);

    const [ width ] = useWindowResizeObserver();

    /**
     * Handle video size change when browser window changes.
     */
    useEffect(() => {
        if(!boxRef?.current) return;

        const w = data?.settings?.videoWidth || 550;
        const h = data?.settings?.videoHeight || 1176;

        const rect = boxRef.current.getBoundingClientRect();
        const newHeight = rect.width * h / w;

        setHeight(newHeight / 3);
    }, [
        selected,
        width,
        data?.settings?.videoWidth,
        data?.settings?.videoHeight,
    ]);

    /**
     * Update current time when video is playing.
     * Also restart video if it has ended, but stop button wasn't clicked.
     */
    useEffect(() => {
        if(!videoRef.current) return;

        const cached = videoRef.current;

        if (isPlaying) {
            videoRef.current.play();
        }
        else {
            videoRef.current.pause();
            setCurrentTime(0);
            videoRef.current.currentTime = 0;
        }

        const handleTimeUpdate = () => {
            setCurrentTime(videoRef.current?.currentTime || 0);
        };

        const handleVideoEnded = () => {
            if(!videoRef.current || !isPlaying) return;

            videoRef.current.currentTime = 0;
            videoRef.current.play();
        };

        cached.addEventListener('timeupdate', handleTimeUpdate);
        cached.addEventListener('ended', handleVideoEnded);

        return () => {
            cached?.removeEventListener('timeupdate', handleTimeUpdate);
            cached?.removeEventListener('ended', handleVideoEnded);
        };
    }, [isPlaying]);

    /**
     * When video changes ---> start new video from the previous time point.
     */
    useEffect(() => {
        if(!videoRef.current) return;

        const cached = videoRef.current;

        const handleLoadedMetadata = () => {
            if(!cached) return;
            cached.currentTime = currentTime;
        };

        cached.addEventListener('loadedmetadata', handleLoadedMetadata);

        return () => {
            cached?.removeEventListener('loadedmetadata', handleLoadedMetadata);
        };
    }, [currentTime]);

    useEffect(() => {
        if(!videoRef.current) return;

        const cached = videoRef.current;

        const canplay = () => {
            if (isPlaying) {
                videoRef.current?.play();
            }
        };

        cached.addEventListener('canplay', canplay);

        return () => {
            cached?.removeEventListener('canplay', canplay);
        };
    }, [selected, isPlaying]);


    const index = getVideoFilterIndex(isBypass, isDelta);

    useEffect(() => {
        videoRef.current?.load();
    }, [selected?.video]);

    return (
        <div
            ref={ boxRef }
            className="bg-purple-100 overflow-hidden max-w-full block"
            style={{
                width: CONTAINER_SIZE,
                height: `${ height }px`,
            }}>
            {
                selected &&
                <video
                    className={`w-full`}
                    muted={ true }
                    style={{
                        transform: `translateY(-${ index * height }px)`,
                        height: 'auto',
                        opacity: isBypass ? 0.7 : 1
                    }}
                    ref={ videoRef }
                    playsInline={ true }>
                    <source
                        type="video/mp4"
                        src={ selected.video }
                    />
                </video>
            }
        </div>
    )
};

export default Video;